import GatewayContainer from '../config/GatewayContainer';
import GatewayNames from '../constant/GatewayNames';
import CaseClient from '../client/integration/case/CaseClient';
import {
  BatchCloseCaseJob,
  CaseActionHistory,
  CaseCommentLog,
  CaseSettings,
  CloseCaseBulkActionOperation,
  RuleDashboardArgs,
} from '../domain/case/CaseDomains';
import {
  ACTION_NOT_PERMITTED_ERROR,
  BULK_CLOSE_NOT_PERMITTED_ERROR,
  CHAT_WRITE_NOT_PERMITTED_ERROR,
  canPerformBulkClose,
  hasCasesActionPermission,
  hasCasesChatWritePermission,
} from '../util/CaseBulkCloseUtil';
import { query } from '@trlogic/client-js';
import { TransactionData, TransactionReason } from '../domain/stream/transaction/TransactionDomains';
import { GraphConfiguration, GraphNodeInformationConfiguration } from '../domain/graph/GraphDomains';
import MessageUtil from '../util/MessageUtil';
import JobClient from '../client/job/JobClient';
import { User } from '../domain/rbac/RbacDomains';
import { store } from '../redux/store';
import { CustomFilter } from '../domain/ui/FilterDomains';
import FilterUtil from '../util/FilterUtil';
import { CancelToken } from 'axios';
import LanguageUtil from '../util/LanguageUtil';

class CaseService {
  private caseClient: CaseClient = GatewayContainer.getInstance().get(GatewayNames.CASE_CLIENT);
  private jobClient: JobClient = GatewayContainer.getInstance().get(GatewayNames.JOB_CLIENT);

  public getCaseCount(domain: string, _query: query.SelectQuery, token?: CancelToken): Promise<number> {
    return this.caseClient.getCaseCount(domain, _query, token);
  }

  public getCaseLogCount(domain: string, _query: query.SelectQuery): Promise<number> {
    return this.caseClient.getCaseLogCount(domain, _query);
  }

  public getActionHistory(ids: string[]): Promise<Record<string, CaseActionHistory>> {
    return this.caseClient.getActionHistory(ids);
  }

  public goCase(domain: string, index: string, transaction: string): Promise<TransactionData> {
    return this.caseClient.goCase(domain, index, transaction);
  }

  public nextCase(domain: string): Promise<{ _source: TransactionData } | undefined> {
    return this.caseClient.nextCase(domain);
  }

  public closeCase(domain: string, index: string, type: string, reason?: string): Promise<void> {
    return this.caseClient.closeCase(domain, index, type, reason);
  }

  public holdCase(domain: string, index: string, reason?: string): Promise<void> {
    return this.caseClient.holdCase(domain, index, reason);
  }

  public async closeBulkCase(
    user: User,
    domain: string,
    ids: CloseCaseBulkActionOperation[],
    type: TransactionReason.FRAUD | TransactionReason.NOT_FRAUD | TransactionReason.IN_COMPLIANT | TransactionReason.COMPLIANT,
    reason?: string,
    caseSettings?: CaseSettings
  ): Promise<'CLOSE_BULK' | 'CASE_BATCH_CLOSE'> {
    if (!canPerformBulkClose(user.username, caseSettings, 'Cases')) {
      throw new Error(BULK_CLOSE_NOT_PERMITTED_ERROR);
    }
    if (ids.length == 0) {
      const filter: CustomFilter = store.getState().page.filter.filter;
      const parsedFilter = FilterUtil.parse(filter);
      const job: BatchCloseCaseJob = {
        domain: domain,
        closeCode: reason,
        type: 'CASE_BATCH_CLOSE',
        caseStatus: type,
        username: user.username,
        status: 'PENDING',
        timestamp: Date.now(),
        filter: parsedFilter,
      };
      await this.jobClient.schedule(job);
      return 'CASE_BATCH_CLOSE';
    }
    await this.caseClient.closeBulk(domain, ids, type, reason);
    return 'CLOSE_BULK';
  }

  public async actionBulk(action: string, ids: TransactionData[]): Promise<void> {
    if (!hasCasesActionPermission()) {
      throw new Error(ACTION_NOT_PERMITTED_ERROR);
    }
    if (ids.length != 0) {
      return this.caseClient.actionBulk(action, ids);
    }
  }

  public commentCase(domain: string, index: string, comment: string): Promise<CaseCommentLog> {
    if (!hasCasesChatWritePermission()) {
      throw new Error(CHAT_WRITE_NOT_PERMITTED_ERROR);
    }
    return this.caseClient.addComment(domain, index, comment);
  }

  public commentEditCase(index: string, comment: string, id: string): Promise<void> {
    if (!hasCasesChatWritePermission()) {
      throw new Error(CHAT_WRITE_NOT_PERMITTED_ERROR);
    }
    return this.caseClient.commentEditCase(index, comment, id);
  }

  public commentDomain(domain: string, comment: string): Promise<void> {
    if (!hasCasesChatWritePermission()) {
      throw new Error(CHAT_WRITE_NOT_PERMITTED_ERROR);
    }
    return this.caseClient.commentDomain(domain, comment);
  }

  public commentEditDomain(domain: string, comment: string, id: string): Promise<void> {
    if (!hasCasesChatWritePermission()) {
      throw new Error(CHAT_WRITE_NOT_PERMITTED_ERROR);
    }
    return this.caseClient.commentEditDomain(domain, comment, id);
  }

  public attachment(domain: string, index: string, file: File): Promise<string> {
    if (!hasCasesChatWritePermission()) {
      throw new Error(CHAT_WRITE_NOT_PERMITTED_ERROR);
    }
    return this.caseClient.addAttachment(domain, index, file);
  }

  public attachmentDomain(domain: string, file: File): Promise<void> {
    if (!hasCasesChatWritePermission()) {
      throw new Error(CHAT_WRITE_NOT_PERMITTED_ERROR);
    }
    return this.caseClient.attachmentDomain(domain, file);
  }

  public getAttachment(file: string): Promise<Blob | string | undefined> {
    return this.caseClient.getAttachment(file);
  }

  public getAttachmentDomain(file: string): Promise<Blob | string | undefined> {
    return this.caseClient.getAttachmentDomain(file);
  }

  public getEnrichmentVariable(enrichments: string[], transactionData: TransactionData): Promise<any> {
    return this.caseClient.getEnrichmentVariable(enrichments, transactionData);
  }

  public getScores(domain: string, options?: GetCaseOptions): Promise<Record<string, number>> {
    return this.caseClient.getScores(domain, options);
  }

  public getDashboard(args?: RuleDashboardArgs): Promise<any> {
    return this.caseClient.getDashboard(args);
  }

  public getNodeInformation(configuration: GraphNodeInformationConfiguration) {
    return this.caseClient.getNodeInformation(configuration);
  }

  public executeGraph(configuration: GraphConfiguration) {
    if (configuration.transaction == undefined || configuration.transaction == '') {
      MessageUtil.warning({
        content: LanguageUtil.get({ id: 'messages.warning.selectTransactionExecute', defaultMessage: '' }),
      });
      return;
    }
    if (configuration.nodeLimit < 1 || configuration.nodeLimit < configuration.groups?.length) {
      MessageUtil.warning({
        content: LanguageUtil.get({ id: 'messages.warning.selectValidNode', defaultMessage: 'Please select a valid node limit' }),
      });
      return;
    }
    if (configuration.groups?.length < 2) {
      MessageUtil.warning({
        content: LanguageUtil.get({ id: 'messages.warning.selectAtLeastTwoNode', defaultMessage: 'Please select as least two node' }),
      });
      return;
    }
    return this.caseClient.executeGraph(configuration);
  }

  public executeSAR = (nodes: any[], selectedTransaction: string) => {
    return this.caseClient.executeSAR(nodes, selectedTransaction);
  };

  public assign(domain: string, index: string, assign: string) {
    return this.caseClient.assign(domain, index, assign);
  }

  public getAssignableCaseUsers(domain: string): Promise<User[]> {
    return this.caseClient.getAssignableCaseUsers(domain);
  }

  public getCounts(): Promise<Record<string, number>> {
    return this.caseClient.getCaseCounts();
  }

  public getPageData(channel: string | undefined, _query: query.SelectQuery, cancelToken?: CancelToken): Promise<{ data: TransactionData[]; count: number }> {
    return this.caseClient.getCasePageData(channel, _query, cancelToken);
  }
}

export type GetCaseOptions = {
  key?: string;
  keyValue?: string;
  skip?: number;
  limit?: number;
};

export default CaseService;
